import TreasuryDialog from './TreasuryDialog';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import './QuoteReminderModal.css';
import { getCastellerNameWithMote } from '../../utils/castellerName';
import { formatMoney } from '../../utils/money';

function MockupNotification({ title, body }) {
    const notification_style = {
        width: '95%',
        backgroundColor: 'rgb(245,245,245)',
        borderRadius: '10px',
        boxShadow: '0 0 10px 0 rgba(0,0,0,0.5)',
        padding: '10px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
    };

    const notification_title_text_style = {
        fontSize: '1em',
        fontWeight: 'bold',
        color: 'black',
    };

    const notification_body_text_style = {
        fontSize: '0.8em',
        color: 'black',
    };

    const notification_icon_style = {
        width: '50px',
        height: '50px',
        borderRadius: 10,
        backgroundColor: 'white',
    };

    return (
        <div style={notification_style}>
            <div style={{ padding: 10 }}>
                <img style={notification_icon_style} src="/aleta-icon.png" alt="icon" />
            </div>
            <div style={{ padding: 10 }}>
                <div style={notification_title_text_style}>{title}</div>
                <div style={notification_body_text_style}>{body}</div>
            </div>
        </div>
    );
}

function QuoteReminderModal({ 
    visible, 
    onClose, 
    quote, 
    unpaidCastellers, 
    castellersInfo, 
    onSendReminder, 
    sendingNotification,
    getPeriodLabel,
    isSubscription 
}) {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [previewIndex, setPreviewIndex] = useState(0);
    const [selectedRecipients, setSelectedRecipients] = useState([]);

    // Initialize default body and recipients when modal opens
    useEffect(() => {
        if (visible && quote && unpaidCastellers.length > 0) {
            const periodLabel = quote.titol || getPeriodLabel(quote.tipus, quote.mes, quote.any);

            if (isSubscription) {
                setTitle('Recordatori: subscripció disponible');
                setBody(`Hola {sobrenom}! Et recordem que pots subscriure't a "${periodLabel}". Entra a l'app per activar la teva subscripció.`);
            } else {
                setTitle('Recordatori: quota pendent');
                setBody(`Hola {sobrenom}! Et recordem que tens pendent el pagament de la quota ${periodLabel} (${formatMoney(quote.value)}€). Si us plau, fes el pagament el més aviat possible.`);
            }
            
            // Convert unpaid castellers to the format expected by Select component, filtering out invalid ones
            const recipientOptions = unpaidCastellers
                .filter(casteller => casteller && (casteller.nom || casteller.mote || casteller['primer-cognom']))
                .map(casteller => ({
                    value: casteller.id,
                    label: getDisplayName(casteller),
                    color: casteller.has_notifications ? 'black' : 'red'
                }))
                .filter(option => option.label && option.label !== 'Usuari desconegut');
            
            setSelectedRecipients(recipientOptions);
            setPreviewIndex(0);
        }
    }, [visible, quote, unpaidCastellers, getPeriodLabel, isSubscription]);

    const formatTextForCasteller = (text, casteller) => {
        const nom = casteller.nom || '';
        const cognom1 = casteller['primer-cognom'] || '';
        const cognom2 = casteller['segon-cognom'] || '';
        const cognoms = [cognom1, cognom2].filter(Boolean).join(' ');
        const sobrenom = casteller.mote || casteller.nom || '';
        
        return text
            .replaceAll('{nom}', nom)
            .replaceAll('{cognom}', cognom1)
            .replaceAll('{cognoms}', cognoms)
            .replaceAll('{sobrenom}', sobrenom);
    };

    const getDisplayName = (casteller) => {
        if (!casteller) return 'Usuari desconegut';
        
        const nom = casteller.nom || '';
        const cognom = casteller['primer-cognom'] || '';
        const mote = casteller.mote || '';
        
        // If we have a mote, use it, otherwise use full name
        let displayName = '';
        if (mote) {
            displayName = mote;
        } else if (nom && cognom) {
            displayName = `${nom} ${cognom}`;
        } else if (nom) {
            displayName = nom;
        } else if (cognom) {
            displayName = cognom;
        } else {
            displayName = `Usuari ${casteller.id || 'desconegut'}`;
        }
        
        return (casteller.canalla ? '👶 ' : '') + displayName;
    };

    // Create options for all non-hidden castellers, filtering out invalid ones
    const allCastellerOptions = Object.values(castellersInfo)
        .filter(c => c && !c.hidden && (c.nom || c.mote || c['primer-cognom'])) // Filter out invalid entries
        .map(casteller => ({
            value: casteller.id,
            label: getDisplayName(casteller),
            color: casteller.has_notifications ? 'black' : 'red'
        }))
        .filter(option => option.label && option.label !== 'Usuari desconegut'); // Remove any that still don't have valid names

    const optionStyles = {
        multiValue: (styles, { data, isDisabled }) => ({ ...styles, color: data.color }),
        multiValueLabel: (styles, { data, isDisabled }) => ({ ...styles, color: data.color }),
        option: (styles, { data, isDisabled }) => ({ ...styles, color: data.color })
    };


    const handleSendReminder = () => {
        // Convert selected recipients back to casteller objects
        const recipientCastellers = selectedRecipients.map(recipient => 
            castellersInfo[recipient.value]
        ).filter(Boolean);

        onSendReminder({
            title,
            body,
            recipients: recipientCastellers
        });
    };

    const getCurrentPreviewCasteller = () => {
        if (selectedRecipients.length === 0) return null;
        const recipientId = selectedRecipients[previewIndex % selectedRecipients.length]?.value;
        return castellersInfo[recipientId];
    };

    if (!visible) return null;

    const currentPreviewCasteller = getCurrentPreviewCasteller();

    return (
        <TreasuryDialog label="Gestionar recordatoris" className="reminder-modal" onClose={onClose} closeDisabled={sendingNotification}>
            <div className="modal-header">
                <h3>{isSubscription ? 'Enviar recordatori de subscripció' : 'Enviar recordatori de pagament'}</h3>
                <button className="close-button" aria-label="Tancar" onClick={onClose} disabled={sendingNotification}>×</button>
            </div>

            <div className="reminder-content treasury-dialog-body">
                <div className="reminder-info">
                    <p className="info-text">
                        {isSubscription ? 'Configura el recordatori per la subscripció:' : 'Configura el recordatori per la quota:'}
                    </p>
                    <div className="quote-info-box">
                        <strong>{getPeriodLabel(quote.tipus, quote.mes, quote.any)}</strong>
                        {!isSubscription ? (
                            <span className="quote-amount">{formatMoney(quote.value)}€</span>
                        ) : null}
                    </div>
                </div>

                <div className="recipients-section">
                    <div className="recipients-selector">
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h5 style={{ flex: 3 }}>
                                Destinataris ({selectedRecipients.filter(u => u.color === 'black').length} + <span style={{ color: 'red' }}>{selectedRecipients.filter(u => u.color === 'red').length}</span> 🔕)
                            </h5>
                            <button
                                type="button"
                                disabled={sendingNotification || !selectedRecipients.some(user => user.color === 'red')}
                                style={{
                                    flex: 1,
                                    fontSize: 12,
                                    textAlign: 'center',
                                    opacity: selectedRecipients.filter(user => user.color === 'red').length > 0 ? 1 : 0,
                                    color: 'red',
                                    padding: 5,
                                    borderColor: 'red',
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderRadius: 10,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedRecipients(selectedRecipients.filter(user => user.color !== 'red'))}
                            >
                                Treure els 🔕
                            </button>
                        </div>
                        <div className="recipient-quick-actions">
                            <button type="button" disabled={sendingNotification} onClick={() => setSelectedRecipients(allCastellerOptions)}>Seleccionar tots</button>
                            <button type="button" disabled={sendingNotification} onClick={() => setSelectedRecipients([])}>Treure tots</button>
                        </div>
                        <Select
                            options={allCastellerOptions}
                            isMulti
                            name="recipients"
                            aria-label="Destinataris del recordatori"
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={selectedRecipients}
                            onChange={items => setSelectedRecipients(items || [])}
                            styles={optionStyles}
                            placeholder={allCastellerOptions.length > 0 ? "Selecciona els destinataris..." : "No hi ha destinataris disponibles"}
                            isDisabled={allCastellerOptions.length === 0}
                        />
                        <p style={{ fontSize: 12 }}>
                            Els usuaris en <span style={{ color: 'red' }}>vermell</span> no tenen les notificacions activades o no tenen l'Aleta instal·lada. No rebran la notificació.
                        </p>
                        {allCastellerOptions.length === 0 && (
                            <p style={{ fontSize: 14, color: '#dc3545', fontWeight: 'bold' }}>
                                No s'han trobat destinataris vàlids. Comprova que hi hagi castellers amb noms configurats correctament.
                            </p>
                        )}
                    </div>
                </div>

                <div className="notification-editor">
                    <div className="form-group">
                        <label htmlFor="reminder-title">Títol de la notificació</label>
                        <input
                            id="reminder-title"
                            type="text"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            maxLength={50}
                        />
                        <small>Pots usar {'{nom}'}, {'{cognom}'} i {'{sobrenom}'} per personalitzar</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="reminder-body">Missatge</label>
                        <textarea
                            id="reminder-body"
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            rows={4}
                            maxLength={200}
                        />
                        <small>Pots usar {'{nom}'}, {'{cognom}'} i {'{sobrenom}'} per personalitzar</small>
                    </div>
                </div>

                {selectedRecipients.length > 0 && (
                    <div className="preview-section">
                        <h4>Previsualització</h4>
                        <div className="preview-controls">
                            <button
                                onClick={() => setPreviewIndex(Math.max(0, previewIndex - 1))}
                                disabled={previewIndex === 0}
                            >
                                ← Anterior
                            </button>
                            <span className="preview-casteller">
                                {currentPreviewCasteller && (
                                    getCastellerNameWithMote(currentPreviewCasteller)
                                )}
                            </span>
                            <button
                                onClick={() => setPreviewIndex(Math.min(selectedRecipients.length - 1, previewIndex + 1))}
                                disabled={previewIndex === selectedRecipients.length - 1}
                            >
                                Següent →
                            </button>
                        </div>
                        {currentPreviewCasteller && (
                            <div style={{ marginTop: 15, marginBottom: 15 }}>
                                <MockupNotification
                                    title={formatTextForCasteller(title, currentPreviewCasteller)}
                                    body={formatTextForCasteller(body, currentPreviewCasteller)}
                                />
                            </div>
                        )}
                    </div>
                )}

            </div>
            <div className="modal-actions treasury-dialog-footer">
                <button
                    className="send-reminder-button"
                    onClick={handleSendReminder}
                    disabled={sendingNotification || selectedRecipients.length === 0}
                >
                    {sendingNotification ? 'Enviant...' : `Enviar recordatori a ${selectedRecipients.length} castellers`}
                </button>
                <button
                    className="cancel-button"
                    onClick={onClose}
                    disabled={sendingNotification}
                >
                    Cancel·lar
                </button>
            </div>
        </TreasuryDialog>
    );
}

export default QuoteReminderModal;
